import React from 'react';
import { Box, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';

/**
 * "Mark as Paid" — one control for the manual-booking tabs and pack assign.
 *
 * Was a checkbox on the walk-in tab and a toggle BUTTON on the session and
 * event tabs, whose label read "Mark as Paid (pending if unchecked)". That
 * sentence describes a checkbox while being rendered as a button, so there was
 * nothing on screen to check and no way to tell the two states apart at a
 * glance — an operator could not see whether the booking they were about to
 * create was paid or owing.
 *
 * A real checkbox plus a Paid/Pending pill says both things at once: what the
 * control does, and what it is currently set to.
 */
const MarkAsPaidCheckbox: React.FC<{
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ checked, onChange }) => (
  <Box
    onClick={() => onChange(!checked)}
    role="checkbox"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={(e) => {
      // A div pretending to be a checkbox has to answer the keyboard like one.
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onChange(!checked);
      }
    }}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 2,
      cursor: 'pointer',
      userSelect: 'none',
    }}
  >
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: '4px',
        border: '2px solid',
        borderColor: checked ? 'success.main' : 'grey.400',
        bgcolor: checked ? 'success.main' : 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {checked && <Check sx={{ fontSize: 14, color: 'white' }} />}
    </Box>
    <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
      Mark as Paid
    </Typography>
    <Box
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 10,
        border: '1px solid',
        borderColor: checked ? 'success.light' : 'warning.light',
        bgcolor: checked ? 'success.50' : 'warning.50',
      }}
    >
      <Typography
        variant="caption"
        fontWeight={500}
        color={checked ? 'success.dark' : 'warning.dark'}
      >
        {checked ? 'Paid' : 'Pending'}
      </Typography>
    </Box>
  </Box>
);

export default MarkAsPaidCheckbox;
